import { computed, ref } from 'vue'

import { useMutation } from '@tanstack/vue-query'

import { handleApiErrorToast } from '@/apiClient'
import { PublishingApi } from '@/apiClient/modules/publishing'
import { WebsitesApi } from '@/apiClient/modules/websites'
import { chatWebsiteQueryKey } from '@/apiClient/queries/useChatQueries'
import type {
  IWebsiteDetail,
  IWebsitePermissionRequest,
} from '@/apiClient/types/websites'
import { queryClient } from '@/lib/queryClient'
import { useChatStore } from '@/store/chat'
import { usePreviewStore } from '@/store/preview'
import { isValidWebsiteSlug } from '@/utils/websiteSlug'

export type WebsiteSlugStatus =
  'idle' | 'checking' | 'available' | 'taken' | 'invalid'

export function useWebsitePublish() {
  const slugStatus = ref<WebsiteSlugStatus>('idle')
  let slugCheckToken = 0

  const checkSlugAvailability = async (
    slug: string,
    options?: {
      websiteId?: string | null
      ownedSlug?: string | null
    },
  ) => {
    const token = ++slugCheckToken
    if (!isValidWebsiteSlug(slug)) {
      slugStatus.value = 'invalid'
      return false
    }

    if (options?.websiteId && options.ownedSlug && slug === options.ownedSlug) {
      slugStatus.value = 'available'
      return true
    }

    slugStatus.value = 'checking'
    try {
      const result = await WebsitesApi.checkSlug({ slug })
      if (token !== slugCheckToken) return result.available
      slugStatus.value = result.available ? 'available' : 'taken'
      return result.available
    } catch {
      if (token === slugCheckToken) slugStatus.value = 'idle'
      return false
    }
  }

  const publishMutation = useMutation({
    mutationFn: async (input: {
      htmlContent?: string
      slug: string
      name: string
      description?: string | null
      is_public: boolean
      password?: string
      remove_password?: boolean
      permissions?: IWebsitePermissionRequest[]
      websiteId?: string | null
      publishPath?: string | null
      htmlPublishKey?: {
        chatId: string
        messageId: string
        blockIndex: number
      }
    }) => {
      const {
        htmlContent,
        slug,
        name,
        description,
        is_public,
        password,
        remove_password,
        permissions,
        websiteId,
        publishPath,
        htmlPublishKey,
      } = input

      let legacyPublishUrl: string | undefined

      if (publishPath) {
        const published = await PublishingApi.publishHtml(publishPath, {})
        legacyPublishUrl = published.url
        if (htmlPublishKey) {
          useChatStore().markAssistantHtmlBlockPublished(
            published.chat_id,
            published.message_id,
            published.block_index,
            published.url,
          )
        }
      }

      const websiteBody = {
        name,
        slug,
        description: description?.trim() || null,
        // A library settings edit sends no HTML (see ModalPublishToWeb): omit `file`
        // so the backend leaves the served page unchanged. Only flows that pass fresh
        // inline HTML (chat-preview publish) overwrite it — the website holds exactly
        // one file, so every publish is a full overwrite (EVGPT-1592).
        ...(htmlContent ? { file: { html_content: htmlContent } } : {}),
        is_published: true,
        is_public,
        ...(password ? { password } : {}),
        ...(remove_password ? { remove_password } : {}),
        ...(permissions !== undefined ? { permissions } : {}),
        // Persist which chat block this website is published from so the chat
        // response can report publish state per block (evergpt-api source link).
        ...(htmlPublishKey
          ? {
              source_chat_id: htmlPublishKey.chatId,
              source_message_id: htmlPublishKey.messageId,
              source_block_index: htmlPublishKey.blockIndex,
            }
          : {}),
      }

      let website: IWebsiteDetail

      if (websiteId) {
        website = await WebsitesApi.update(websiteId, websiteBody)
      } else {
        website = await WebsitesApi.create(websiteBody)
      }

      return { website, legacyPublishUrl }
    },
    onSuccess: ({ website, legacyPublishUrl }) => {
      const previewStore = usePreviewStore()
      const chatStore = useChatStore()
      const content = previewStore.content
      const publishKey =
        content?.type === 'html' ? content.htmlPublishKey : undefined
      previewStore.updateHtmlPreview({
        websiteId: website.id,
        websiteSlug: website.slug,
        websiteName: website.name,
        websiteDescription: website.description ?? '',
        websiteOwnedSlug: website.slug,
        isWebsitePublished: website.is_published,
      })
      if (publishKey) {
        // Optimistically stamp the block's HTML file so the chat asset card reads
        // publish state from `file.metadata` immediately; the backend persists the
        // same link (website source) and re-derives it on the next chat load.
        chatStore.setAssistantHtmlBlockWebsite(
          publishKey.chatId,
          publishKey.messageId,
          publishKey.blockIndex,
          { websiteId: website.id, isPublished: website.is_published },
        )
        // Publishing this block onto a website another block already owned
        // (override) evicts those blocks: the backend moves the website's source
        // link here, so they end up unlinked. Clear their stale link now so their
        // cards drop "Published" without waiting for a chat refetch (EVGPT-1474).
        chatStore.clearAssistantHtmlBlocksForWebsite(
          publishKey.chatId,
          website.id,
          {
            messageId: publishKey.messageId,
            blockIndex: publishKey.blockIndex,
          },
        )
      }
      if (legacyPublishUrl) {
        previewStore.setOpenPreviewPublishedHtmlUrl(legacyPublishUrl)
      }
      // This website is now the chat's website (GET /chats/{id}/website). Seed that
      // query with the fresh payload so the toolbar and the publish modal read the
      // new publish state without a round trip.
      const sourceChatId = website.source_chat_id ?? publishKey?.chatId
      if (sourceChatId) {
        queryClient.setQueryData(chatWebsiteQueryKey(sourceChatId), website)
      }
      // The publish may also have moved another chat's page here, so mark every
      // other chat's website stale. `refetchType: 'none'` because only the seeded
      // chat has a mounted observer: without it the broad key prefix-matches the
      // seed and immediately refetches it, undoing the round trip just saved.
      queryClient.invalidateQueries({
        queryKey: chatWebsiteQueryKey(),
        refetchType: 'none',
      })
      // Note: when this block moves to a *different* website, the previous website
      // is unpublished + unlinked from the block by the backend (one website per
      // source block — EVGPT-1474 `_supersede_prior_source_links`), so it stops
      // serving the overridden page and the block re-resolves to the new site.
      queryClient.invalidateQueries({ queryKey: ['websites'] })
    },
    onError: handleApiErrorToast,
  })

  // `publishExistingWebsite` fetches the website before the mutation starts, and
  // `publishMutation.isPending` only flips once the mutation runs — so the button
  // guard would leave a gap during the GET and allow a double-submit. Track the
  // pre-publish fetch too, so `isPublishing` covers the whole flow.
  const isFetchingWebsite = ref(false)
  const isPublishing = computed(
    () => isFetchingWebsite.value || publishMutation.isPending.value,
  )

  const publishExistingWebsite = async (input: {
    websiteId: string
    htmlContent: string
    publishPath?: string | null
    htmlPublishKey?: {
      chatId: string
      messageId: string
      blockIndex: number
    }
  }) => {
    isFetchingWebsite.value = true
    try {
      const website = await WebsitesApi.get(input.websiteId)
      return await publishMutation.mutateAsync({
        htmlContent: input.htmlContent,
        slug: website.slug,
        name: website.name,
        description: website.description,
        is_public: website.is_public,
        websiteId: website.id,
        publishPath: input.publishPath,
        htmlPublishKey: input.htmlPublishKey,
      })
    } finally {
      isFetchingWebsite.value = false
    }
  }

  return {
    slugStatus,
    checkSlugAvailability,
    publishWebsite: publishMutation.mutateAsync,
    publishExistingWebsite,
    isPublishing,
    publishMutation,
  }
}
